import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Veuillez sélectionner une image."); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error("Image trop volumineuse (5 Mo max)."); return; }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.url);
            toast.success("Image téléversée");
        } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
    };

    const onDrop = (e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); };

    return (
        <div data-testid={testId}>
            <label className="overline block mb-2">{label}</label>
            {value ? (
                <div className="relative group inline-block border border-brand-border bg-brand-bgalt">
                    <img src={value} alt="" className="block max-h-48 object-cover" />
                    <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 bg-brand-ink/90 text-brand-bg p-1.5 opacity-0 group-hover:opacity-100 transition" data-testid={`${testId}-remove`}><X size={14} /></button>
                </div>
            ) : (
                <div onClick={() => !uploading && inputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={`cursor-pointer border-2 border-dashed transition-colors px-6 py-10 text-center ${dragOver ? "border-brand-sage bg-brand-sage/10" : "border-brand-border bg-brand-bgalt hover:border-brand-sage"} ${uploading ? "opacity-60 cursor-wait" : ""}`}>
                    {uploading ? (<><Loader2 className="mx-auto animate-spin text-brand-sage" size={22} /><p className="mt-2 text-xs text-brand-sageDark">Téléversement…</p></>) : (<><Upload className="mx-auto text-brand-sage" size={22} /><p className="mt-2 text-sm text-brand-ink">Cliquer ou glisser une image</p><p className="text-xs text-brand-sageDark">JPG · PNG · WebP — 5 Mo max</p></>)}
                </div>
            )}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
    );
}

export function GalleryUpload({ value = [], onChange, label = "Galerie", testId = "gallery-upload" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const uploadFiles = async (files) => {
        const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
        if (!list.length) return;
        setUploading(true);
        const urls = [...value];
        for (const file of list) {
            if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} ignoré (> 5 Mo).`); continue; }
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            } catch (e) { toast.error(`${file.name} : ${formatApiError(e)}`); }
        }
        onChange(urls);
        setUploading(false);
    };

    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    const move = (i, dir) => { const arr = [...value]; const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; onChange(arr); };

    return (
        <div data-testid={testId}>
            <label className="overline block mb-2">{label}</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {value.map((url, i) => (
                    <div key={i} className="relative group border border-brand-border bg-brand-bgalt">
                        <img src={url} alt="" className="aspect-square w-full object-cover" />
                        <div className="absolute inset-0 bg-brand-ink/0 group-hover:bg-brand-ink/40 transition flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="bg-brand-bg/90 px-2 py-1 text-xs disabled:opacity-30">←</button>
                            <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} className="bg-brand-bg/90 px-2 py-1 text-xs disabled:opacity-30">→</button>
                            <button type="button" onClick={() => remove(i)} className="bg-red-600 text-white px-2 py-1 text-xs"><X size={12} /></button>
                        </div>
                    </div>
                ))}
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="aspect-square border-2 border-dashed border-brand-border bg-brand-bgalt hover:border-brand-sage flex flex-col items-center justify-center gap-2 transition">
                    {uploading ? <Loader2 className="animate-spin text-brand-sage" size={20} /> : <><ImageIcon size={20} className="text-brand-sage" /><span className="text-xs text-brand-sageDark">Ajouter</span></>}
                </button>
            </div>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
        </div>
    );
}
